import { HttpClient, HttpResponse, RequestOptions } from '@wix/http-client';

import { createHttpClient } from './http-client';

const parseBody = (response: HttpResponse) => response?.data;
const parseHeadersAndBody = (response: HttpResponse) => ({
  body: response.data,
  headers: response.headers,
});

type GetStringFn = () => string;

const withDefaultConfigs = (
  baseURL: string,
  path: string,
  instance: string | undefined,
  locale: string | undefined,
  getInstance: GetStringFn | undefined,
) => (config: RequestConfig) => {
  config = {
    ...config,
    method: config?.method ?? 'GET',
    url: path ? baseURL + path.valueOf() : baseURL,
    data: config?.data ?? {},
    headers: {
      common: {
        locale: config?.headers?.locale || locale || 'en',
      },
    },
  };
  if (getInstance || instance) {
    config.headers.Authorization = getInstance ? getInstance() : instance;
  }
  return config;
};

interface RequestConfig extends RequestOptions {
  parseHeaders?: boolean;
}

export default function createRequest({
  httpClientInstance,
  baseUrl = '',
  instance,
  locale,
  getInstance,
}: {
  httpClientInstance?: HttpClient;
  baseUrl: string;
  instance?: string;
  locale?: string;
  getInstance: () => string;
}) {
  const httpClient = !httpClientInstance ? createHttpClient(baseUrl, getInstance) : httpClientInstance;

  const request = (path: string, config: RequestConfig) => {
    const cfg = withDefaultConfigs(baseUrl, path, instance, locale, getInstance)(config);
    return httpClient
      ?.request(cfg)
      .then(config?.parseHeaders ? parseHeadersAndBody : parseBody)
      .catch((e) => Promise.reject(e.response));
  };

  request.get = (path: string, data?: any, config?: RequestConfig) => request(path, { ...config, method: 'GET', data });
  request.post = (path: string, data: any, config?: RequestConfig) =>
    request(path, { ...config, method: 'POST', data });

  request.put = (path: string, data: any, config?: RequestConfig) => request(path, { ...config, method: 'PUT', data });
  request.patch = (path: string, data: any, config?: RequestConfig) =>
    request(path, { ...config, method: 'PATCH', data });
  request.delete = (path: string, config?: RequestConfig) => request(path, { ...config, method: 'DELETE' });

  request.getCancelToken = () => httpClient.CancelToken.source();

  return request;
}
